import React, { useRef } from 'react';
import { Box, Flex, Row, Text } from '@keyslabor/shared/ui';
import CalendarBase from '../ReactCalendar';
import { CurWeekBar } from './Calendar.elements';
import { WIDTH, activeTileClass, curWeekBarHeight, weekday } from './constants';
import CalendarNavigation from './CalendarNavigation';
import { calendarStyles, weekSelectStyle } from './styles';
import { formatShortWeekday } from './utils';
import { CalendarProps } from './types';

import useHandleWeekSelBar from './useHandleWeekSelBar';
import { BoxStyledProps } from '@keyslabor/shared/ui/layout/Box/Box.types';
import useCalendarUtils from '@/hooks/useCalendarUtils.ts';
import { useCalendarConfig } from '@/hooks/useCalendarConfig.ts';

export default function Calendar({
  boxShadow,
  onDateSelect,
  aboveNavText,
  maxDate = null,
  minDate = null,
  navigationPosition = 'bottom',
  calendarProps,
  maxWeek,
  minWeek,
  value,
  topDate,
  showTopDate,
  weekSelect,
  highlightWeekOffset = true,
  respectStartWorkWeek,
  freezeDay,
  showCurrWeekBar = false,
  topDateType,
  width,
  navHeight,
  navType,
  ...rest
}: CalendarProps & BoxStyledProps) {
  const curWeekBarRef = useRef<HTMLDivElement>(null);
  const calendarWrapperRef = useRef<HTMLDivElement>(null);
  const isBottom = navigationPosition === 'bottom';
  const isTop = navigationPosition === 'top';
  const utils = useCalendarUtils();
  const calendarConfig = useCalendarConfig();

  const { year } = utils.getFormattedDateNames(value);

  useHandleWeekSelBar({
    activeTileClass,
    calendarWrapperRef,
    curWeekBarRef,
  });

  const nav = (
    <CalendarNavigation
      position={navigationPosition}
      weekSelect={weekSelect}
      value={value}
      withCalendarIcon={isBottom}
      onDateSelect={onDateSelect}
      topDateType={topDateType}
      height={navHeight}
      navType={navType}
    />
  );

  return (
    <Box
      bg="white"
      // pt="21px"
      boxShadow={boxShadow}
      display="grid"
      gridAutoRows="max-content 1fr"
      justifyContent="center"
      css={[calendarStyles, weekSelect && weekSelectStyle]}
      {...rest}
      width={width || WIDTH}
      px="0"
    >
      {showTopDate && (
        <Flex pb="1rem" flexCenter>
          <Text fontSize="16px" fontWeight={500}>
            {topDate ?? `${utils.getNiceDate(value)}, ${year}`}
          </Text>
        </Flex>
      )}
      <Row height="100%">
        {isTop && aboveNavText && (
          <Text className="aboveTextNav">{aboveNavText}</Text>
        )}
        {isTop ? nav : null}
        <Box
          width={WIDTH}
          position="relative"
          ref={calendarWrapperRef}
          height="100%"
          mt={isTop ? '12px' : ''}
        >
          <Flex alignItems="center" className="weekNumberIndex">
            <Text fontSize="10px">#</Text>
          </Flex>
          {showCurrWeekBar && (
            <CurWeekBar ref={curWeekBarRef} height={curWeekBarHeight} />
          )}

          {/* Note:in order to sync week days with with custom weekStart, calendar type Iso is overrided by custom customWeekStartDay,  */}
          {/* if any further changes needed */}
          {/*  */}
          <CalendarBase
            customWeekStartDay={calendarConfig.firstDayOffset}
            customWeeksOffset={calendarConfig.weeksOffset}
            calendarType={
              calendarConfig.firstDayOffset === 0 ? 'US' : 'ISO 8601'
            }
            formatShortWeekday={formatShortWeekday(weekday)}
            tileContent={({ date }) =>
              utils.titleContent({
                weekSelect,
                highlightWeekOffset,
                date,
              })
            }
            key={value?.valueOf()}
            showNavigation={false}
            value={value}
            showWeekNumbers
            onClickDay={onDateSelect}
            maxDate={maxDate}
            minDate={minDate}
            tileDisabled={({ date }) =>
              utils.titleFormater({ date, maxWeek, minWeek })
            }
            {...calendarProps}
          />
        </Box>
        {isBottom && aboveNavText && (
          <Text className="aboveTextNav">{aboveNavText}</Text>
        )}
        {isBottom ? nav : null}
      </Row>
    </Box>
  );
}
